import AxiosFoundationAPI from "../../../../util/AxiosFoundationAPI";
import EnterpriseComponent from "../../EnterpriseComponent";
import {v4 as uuidv4} from "uuid";
import {V1ChangeOrderTaskIDOCToArray} from "../../../../util/Payload";

// The tasks on a change order (t1/v1/change_order_tasks).
//
// THE UPDATE PATH IS A COMPLETION, NOT AN EDIT. PUT marks a task DONE or ABANDONED and carries
// nothing else: the instruction is content of the change order and may only be written while it is a
// draft, while reporting that the work is finished stays available long after the signatures are in.
// Routing both through one update would mean relaxing one of those rules to let the other through.
const URL = "t1/v1/change_order_tasks";
const IDOC = "v1.ChangeOrderTask-001";

class ChangeOrderTaskEnterpriseComponent extends EnterpriseComponent {

    performQuery = (page, pageSize, foreignKey) => {
        page = page || 1;
        pageSize = pageSize || this.getDefaultCacheSize();

        this.notifyLoadStartListeners();

        const idocResponse = this.getIDOCResponse() ?? IDOC;

        const members = [];
        const queryPayload = this.prepareQueryPayload();
        if (queryPayload.size > 0) {
            for (const [key, value] of queryPayload) {
                if (key.includes("_query")) continue;
                let operator = "EQUAL";
                if (queryPayload.get(key + "_query") !== undefined) operator = queryPayload.get(key + "_query");
                members.push({"Type": "Attribute", "Name": key, "Value": value, "Operator": operator});
            }
        }

        const QBE = {
            "PlatformMessage": {
                "-idoc": idocResponse,
                "-id": uuidv4(),
                "ChangeOrderTask": {
                    "_attributes": {"limit": pageSize, "sort": this.getFormattedSortSpecification(), "offset": (page - 1) * pageSize},
                    "QueryGroups": {"QueryGroup": [{"Name": "primary", "Operator": "AND", "Type": "QueryGroup", "Membership": {"Member": members}}]}
                }
            }
        };

        AxiosFoundationAPI({
            method: "GET",
            url: URL,
            json: true,
            params: {"QBE": btoa(JSON.stringify(QBE))},
            headers: {
                "visibility-mode": this.getVisibilityMode(),
                "visibility-value": this.getVisibilityValue(),
                "idoc-response": idocResponse,
            },
        }).then(response => {
            this.fetchEnterpriseComponentSuccess(V1ChangeOrderTaskIDOCToArray(response.data), page, pageSize);
        }).catch(error => {
            this.fetchEnterpriseComponentFailed(error);
        });
    };

    buildPayload = () => {
        const r = {};
        r["USID"] = this.getAttributeValue("USID");
        // FALLS BACK TO THE SCOPE, because a task added from a change order's panel belongs to that
        // change order and nothing in the dialog asks for it. newRecord() applies defaults and the
        // realm but NOT the scope.
        r["ChangeOrderUSID"] = this.getAttributeValue("ChangeOrderUSID")
            || (this.getScope && this.getScope().get ? this.getScope().get("ChangeOrderUSID") : null);
        r["Instruction"] = this.getAttributeValue("Instruction");
        r["AssigneeUSID"] = this.getAttributeValue("AssigneeUSID");
        // Status is stamped server-side: a task starts in step with the change order that carries it.
        return r;
    };

    /** Mark a task DONE or ABANDONED. */
    complete = (usid, status) => {
        const payload = {PlatformMessage: {"-idoc": IDOC, ChangeOrderTask: {USID: usid, Status: status}}};
        return AxiosFoundationAPI({method: "PUT", url: URL, data: payload,
                                   headers: {"idoc-request": IDOC, "idoc-response": IDOC}});
    };

    enterpriseComponentPostIDOC = () => {
        const payload = {};
        payload.PlatformMessage = {};
        payload.PlatformMessage["-idoc"] = IDOC;
        payload.PlatformMessage.ChangeOrderTask = this.buildPayload();
        return payload;
    };

    performInsert() {
        const options = {method: "POST", url: URL, json: true, data: this.enterpriseComponentPostIDOC(),
                         headers: {"idoc-request": IDOC, "idoc-response": IDOC}};
        this.notifyInsertStartListeners();
        AxiosFoundationAPI(options)
            .then(response => { this.insertEnterpriseComponentSuccess(V1ChangeOrderTaskIDOCToArray(response.data)); })
            .catch(error => { this.insertEnterpriseComponentFailed(error); });
    };

    performDelete() {
        const payload = {};
        payload.PlatformMessage = {};
        payload.PlatformMessage["-idoc"] = IDOC;
        payload.PlatformMessage.ChangeOrderTask = {"USID": this.getAttributeValue("USID")};
        const options = {method: "DELETE", url: URL, json: true, data: payload,
                         headers: {"idoc-request": IDOC, "idoc-response": IDOC}};
        this.notifyDeleteStartListeners();
        AxiosFoundationAPI(options)
            .then(() => { this.deleteEnterpriseComponentSuccess(); })
            .catch(error => { this.deleteEnterpriseComponentFailed(error); });
    };
}

export default ChangeOrderTaskEnterpriseComponent;
